/**
 * @version 2.0
 * @author Kelnik Studios {http://kelnik.ru}
 * @link https://gitbook.kelnik.ru/main/front-end/components/popup.html documentation
 */

/**
 * DEPENDENCIES
 */
import {InternetConnection, Utils} from '@/common/scripts/utils';
import Swiper, {Navigation} from 'swiper';
import basicTemplate from './templates/basic-popup.twig';
import constructorTemplate from './templates/constructor-popup.twig';
import defaultAjaxTemplate from './templates/ajax-popup.twig';
import finishingTemplate from './templates/finishing-popup.twig';
import galleryTemplate from './templates/gallery-popup.twig';
import {initConstructor} from '@/common/scripts/common';
import mapTemplate from './templates/map-popup.twig';
import mediaTemplate from './templates/media-popup.twig';
import Observer from '@/common/scripts/observer';
import {PopupFacade} from '@/components/popup/popupFacade';
import saleAjaxTemplate from './templates/sale-popup.twig';
import searcherTemplate from './templates/searcher-popup.twig';
import tabsAjaxTemplate from './templates/ajax-popup-tabs.twig';
import {YANDEX_API_URL} from '@/common/scripts/constants';
import YandexMap from '../yandex-map';

const observer = new Observer();
const emptyFunction = () => {
    return null;
};
const openedPopups = new Set();
const ajaxTemplates = {
    defaultAjaxTemplate,
    tabsAjaxTemplate
};

export class AbstractPopup {
    constructor(options = {}) {
        this.openStateClass = options.openStateClass || 'popup_state_open';
        this.animationCloseClass = options.animationCloseClass || 'popup_close_animation';
        this.closeButtonAriaLabel = options.closeButtonAriaLabel || 'Закрыть всплывающее окно';
        this.openBtns = options.openButtons;
        this.currentState = false;
        this.onCreate = options.onCreate || emptyFunction;
        this.onClose = options.onClose || emptyFunction;

        /* Определяется в методе _getElements() */
        this.popup = null;
        this.content = null;
        this.closeBtns = null;

        /* Определяется в методе _setTemplates() */
        this.template = null;

        /* Определяется в методе _setData() */
        this.data = null;
        this.modify = options.modify || '';

        this.sale = options.sale || '';

        this._bindMethodsContext();
    }

    /**
     * @Public
     * Входная точка проекта
     *
     */
    init() {
        this._bindOpenEvents();
    }

    /**
     * @Public
     * Отображение попапа на экране
     *
     */
    showPopup() {
        this.popup.classList.remove(this.animationCloseClass);
        this.popup.classList.add(this.openStateClass);

        const isFlatPopup = this.popup.querySelector('.j-flat');
        const popupInner = this.popup.querySelector('.j-popup-inner-wrapper');


        Utils.bodyFixed(isFlatPopup ? popupInner : this.popup);

        if (isFlatPopup) {
            observer.subscribe('searcherPopup:close', () => {
                Utils.bodyFixed();

                Utils.bodyFixed(popupInner);
            });
        }
    }

    /**
     * @Public
     * Открытие попапа
     *
     */
    displayPopup() {
        this.showPopup();

        observer.publish('popup:open', this);

        this.currentState = true;
    }

    /**
     * @Public
     * Сокрытие попапа с дисплея
     *
     */
    hidePopup() {
        this.popup.classList.remove(this.openStateClass);

        Utils.bodyStatic();
    }

    _showCloseAnimate() {
        this.popup.classList.add(this.animationCloseClass);
    }

    /**
     * @Public
     * Удаление попапа из DOM-дерева
     *
     */
    removePopup() {
        if (!this.currentState) {
            return;
        }

        this._showCloseAnimate();

        setTimeout(() => {
            this.hidePopup();
            Utils.removeElement(this.popup);

            observer.publish('popup:close', this);

            this.currentState = false;

            document.removeEventListener('keyup', this._closeByKeyboard);
            document.removeEventListener('click', this._closeByMouse);

            this.onClose();
        }, 300);

        if (document.referrer.includes('visual-search') && window.location.href.includes('gallery#gallery')) {
            window.location.href = '/visual-search';
        }
    }

    /**
     * @Public
     * Перезапуск попапа
     *
     */
    reOpen() {
        this.removePopup();
        this._open();
    }

    /**
     * @Private
     * Бинд контекста для методов класса
     *
     */
    _bindMethodsContext() {
        this._closeByKeyboard = this._closeByKeyboard.bind(this);
        this.removePopup = this.removePopup.bind(this);
    }

    /**
     * @Private
     * Установка шаблона попапа
     *
     */
    _setTemplates() {
        this.template = basicTemplate;
    }

    /**
     * @Abstract Private
     * Установка данных в попапе
     *
     */
    _setData() {
        throw new Error(`Method _setData() must be overridden`);
    }

    /**
     * @Private
     * Вставка ноды попапа в DOM-дерево
     *
     */
    _setPopupOnPage() {
        document.body.insertAdjacentHTML('afterbegin', this.template(this.data));
    }

    /**
     * @Private
     * Получение нод DOM-дерева из попапа
     *
     */
    _getElements() {
        this.popup = document.querySelector('.j-popup-body');
        this.content = document.querySelector('.j-popup__content');

        const closeBtn = Array.from(this.popup.querySelectorAll('.j-popup__close'));
        const overLay = this.popup.querySelector('.j-popup__overlay');

        this.closeBtns = closeBtn.concat(overLay);
    }

    /**
     * @Private
     * Генерация попапа
     *
     */
    _createPopup() {
        this._setData();
        this._setTemplates();
        this._setPopupOnPage();
        this._getElements();
        this._bindCloseEvents();

        this.onCreate();
    }

    /**
     * @Abstract Private
     * Коллбэк для события открытия
     *
     */
    _open() {
        throw new Error(`Method _open() must be overridden`);
    }

    /**
     * @Private
     * Привязка методов к событиям открытия попапа
     *
     */
    _bindOpenEvents() {
        this.openBtns.forEach((button, index) => {
            button.addEventListener('click', () => {
                if (this.currentState) {
                    return;
                }

                this.currentTarget = button;

                this._open();
                this._bindAfterOpenEvents(index);
            });
        });
    }

    /**
     * @Abstract Private
     * Коллбэк для события по клику
     */
    _bindAfterOpenEvents() {
        throw new Error(`Method _bindAfterOpenEvents() must be overridden`);
    }

    /**
     * @Private
     * Привязка методов к событиям закрытия попапа
     *
     */
    _bindCloseEvents() {
        this._closeByMouse();
        document.addEventListener('keyup', this._closeByKeyboard);
    }

    /**
     * @Private
     * Привязка методов к событиям закрытия попапа ко клику мышью
     *
     */
    _closeByMouse() {
        if (this.closeBtns.length < 1) {
            return;
        }

        this.closeBtns.forEach((closeBtn) => {
            if (closeBtn) {
                closeBtn.addEventListener('click', this.removePopup);
            }
        });
    }

    /**
     * @Private
     * Привязка методов к событиям закрытия попапа по вводу с клавиатуры
     * @param {number} key - числовой код нажатой клавиши
     */
    _closeByKeyboard(key) {
        const keyCodeESC = 27;

        if (key.keyCode === keyCodeESC) {
            this.removePopup();
        }
    }

    /**
     * @Private
     * Получение необходимого дата-атрибута с кнопок открытия попапа
     * @param {string} attribute - атрибут, по которому происходит поиск
     * @return {String} - значение дата-атрибута
     *
     */
    _getDataAttrValue(attribute) {
        const attributesArray = [...new Set(this.openBtns.map((x) => {
            return x.getAttribute(attribute);
        }))];

        if (attributesArray.length !== 1 || !attributesArray[0]) {
            return null;
            // throw new Error(`Expect one ${attribute}. You set: ${attributesArray[0] ? attributesArray : 0}`);
        }

        return attributesArray[0];
    }
}

export class StaticPopup extends AbstractPopup {
    constructor(options) {
        super(options);

        /* @Override */
        this.closeButtonAriaLabel = 'Закрыть';
        this.onCreate = this._initNestedPopupViaFacade.bind(this);
    }

    /* @Override */
    _setData() {
        const contentLink = this._getDataAttrValue('data-href');
        const title = this?.currentTarget?.dataset?.title || '';
        const modify = this.currentTarget ? this.currentTarget.dataset.modify : '';
        const flatFinalPlan = this?.currentTarget?.dataset?.flatPlan || '';
        const flatFinalImg = this?.currentTarget?.dataset?.flatImage || '';

        const contentBlock = document.getElementById(contentLink);
        const flatFinalPlanField = contentBlock?.querySelector('input.flatFinalPlan');
        const flatFinalImgField = contentBlock?.querySelector('input.flatFinalImg');

        if (flatFinalPlanField && flatFinalPlan) {
            flatFinalPlanField.value = flatFinalPlan;
        }

        if (flatFinalImgField && flatFinalImg) {
            flatFinalImgField.value = flatFinalImg;
        }

        this.data = {
            title,
            modify,
            content        : document.getElementById(contentLink).innerHTML,
            buttonAriaLabel: this.closeButtonAriaLabel
        };
    }

    /* @Override */
    _open() {
        this._createPopup();
        this.displayPopup();
    }

    /* @Override */
    // eslint-disable-next-line
    _bindAfterOpenEvents() {}

    /**
     * Инициализирует кнопки, открывающие вложенный попап
     */
    _initNestedPopupViaFacade() {
        const buttons = [...this.popup.querySelectorAll('.j-popup')];

        if (!buttons.length) {
            return;
        }

        const options = {
            ...{
                openButtons: buttons,
                onCreate   : () => {
                    this.removePopup();
                    setTimeout(() => {
                        Utils.bodyFixed(this.popup);
                    }, 300);
                },
                onClose: this._open.bind(this)
            }
        };

        const facade = new PopupFacade(options);

        facade.init();

        buttons.forEach((element) => {
            element.setAttribute('data-init', 'true');
            element.setAttribute('data-init-2', 'true');
        });
    }
}

export class StaticPopupWithHash extends StaticPopup {
    /* @Extend */
    _bindOpenEvents() {
        super._bindOpenEvents();

        this._parseUrl();
    }

    /* @Extend */
    displayPopup() {
        super.displayPopup();

        this._setHash();
    }

    /* @Extend */
    removePopup() {
        super.removePopup();

        this._clearHash();
    }

    /**
     * @Private
     * Открывает попап по хэшу в омнибоксе
     */
    _parseUrl() {
        const contentLink = this._getDataAttrValue('data-href');

        if (this.currentState || window.location.hash !== `#${contentLink}`) {
            return;
        }

        this._createPopup();
        this.displayPopup();
    }

    /**
     * @Private
     * Устанавливает хэш попапа в омнибокс браузера
     */
    _setHash() {
        const contentLink = this._getDataAttrValue('data-href');

        window.history.pushState(null, null, `#${contentLink}`);
    }

    /**
     * @Private
     * Очищает омнибокс браузера от хэша попапа
     */
    _clearHash() {
        if (window.history.pushState) {
            window.history.pushState(null, null, window.location.pathname + window.location.search);

            return;
        }

        window.location.hash = '';
    }
}

export class StaticPopupWithHashDynamicNested extends StaticPopupWithHash {
    _setTemplates() {
        this.template = finishingTemplate;
    }

    showPopup() {
        this.popup.classList.remove(this.animationCloseClass);
        this.popup.classList.add(this.openStateClass);

        // Пробуем найти - есть ли у нас класс указывающий на попап отделки
        const finishing = this.popup.querySelector('.j-finishing-wrapper');

        finishing ? Utils.bodyFixed(finishing) : Utils.bodyFixed(this.popup);
    }
}

export class StaticPopupWithMedia extends StaticPopup {
    _setTemplates() {
        this.template = mediaTemplate;
    }

    /* @Override */
    _setData() {
        const src = this.openBtns[0].getAttribute('data-media');
        const title = this.openBtns[0].getAttribute('data-title');
        const alt = this.openBtns[0].getAttribute('data-alt') || title;
        const type = this.openBtns[0].getAttribute('data-type') || 'image';
        const modify = this.modify || this.currentTarget?.dataset?.modify || '';

        this.data = {
            buttonAriaLabel: this.closeButtonAriaLabel,
            modify,
            src,
            title,
            alt,
            type
        };
    }
}

export class StaticPopupWithMap extends StaticPopup {
    _setTemplates() {
        this.template = mapTemplate;
    }

    /* @Override */
    _setData() {
        const json = this._getDataAttrValue('data-json');
        const title = this._getDataAttrValue('data-title');

        this.data = {
            buttonAriaLabel: this.closeButtonAriaLabel,
            modify         : this.modify,
            json,
            title,
            className      : 'j-contacts-map'
        };
    }

    /* @Override */
    _open() {
        this._createPopup();
        this.displayPopup();
        this._initMap();
    }

    _setMapElements() {
        this.map = this.popup.querySelector('.j-contacts-map');
    }

    _setMap() {
        this.ymaps = window.ymaps;
        this.yandexMap = null;

        if (this.ymaps) {
            this.ymaps.ready(() => {
                this.yandexMap = new YandexMap(this.ymaps);

                this.yandexMap.init({
                    wrapper          : this.map,
                    cluster          : true,
                    customZoomControl: true,
                    disableMobileDrag: false
                });
            });
        }
    }

    _initMap() {
        this._setMapElements();

        if (this.ymaps) {
            this._setMap(this.map);
        } else {
            Utils.loadScript(YANDEX_API_URL, this._setMap.bind(this));
        }
    }
}

export class GalleryPopup extends StaticPopup {
    /* @Override */
    _setTemplates() {
        this.template = galleryTemplate;
    }

    _bindAfterOpenEvents() {
        this._initGalleries();
    }

    _initGalleries() {
        const galleries = [...this.popup.querySelectorAll('.j-popup-slider')];

        galleries.forEach((gallery) => {
            const test = new Swiper(gallery, {
                modules         : [Navigation],
                wrapperClass    : 'j-popup-slider__wrapper',
                slideClass      : 'j-popup-slider__slide',
                slidePrevClass  : 'is-prev',
                slideActiveClass: 'is-active',
                slideNextClass  : 'is-next',
                slidesPerView   : 1,
                init            : false,
                navigation      : {
                    nextEl       : '.j-popup-slider__next',
                    prevEl       : '.j-popup-slider__prev',
                    disabledClass: 'is-disabled'
                }
            });

            test.init();
        });
    }
}

export class AjaxPopup extends AbstractPopup {
    constructor(options) {
        super(options);

        /* Определяется в методе _sendDataRequest() */
        this.dataContent = null;

        /* Определяется в методе _resending() */
        this.setInterval = null;

        this._setData = this._setData.bind(this);
        this.serverConnection = new InternetConnection(this._sendDataRequest.bind(this));
    }

    /**
     * @Private
     * Проверка на наличие интернета.
     * @return {String} Метод запроса из атрибута или POST, если атрибут пуст.
     */
    _getQueryType() {
        const attributesArray = [...new Set(this.openBtns.map((x) => {
            return x.getAttribute('data-query');
        }))];

        if (attributesArray.length > 1) {
            throw new Error(`Expect maximum one data-query. You set: ${attributesArray}`);
        }

        return attributesArray[0] || 'GET';
    }

    /**
     * @Private
     * Отправляет запрос на сервер и вызывает создание попапа с данными из ответа сервера
     */
    _sendDataRequest() {
        const that = this;
        const contentLink = this._getDataAttrValue('data-ajax');
        const callback = {
            success(req) {
                that.dataContent = req.data.content;
                that.templateName = req.data.template || 'default';
            },
            error(err) {
                console.error(`Server error with status ${err}`);
            },
            complete() {
                that.dataContent = that.dataContent || that.serverConnection.getErrorMessage();

                that._createPopup();
                that.displayPopup();

                that.dataContent = null;
            }
        };

        Utils.send(null, contentLink, callback, this._getQueryType());
    }

    /* @Override */
    _open() {
        this.serverConnection.connect();
    }

    /* @Override */
    _setData() {
        this.data = {
            title          : this?.currentTarget?.dataset?.title || this.dataContent?.title || '',
            backLink       : this?.currentTarget?.dataset?.backLink || this.dataContent?.backLink || '',
            closeLink      : this?.currentTarget?.dataset?.closeLink || this.dataContent?.closeLink || '',
            content        : this.dataContent,
            buttonAriaLabel: this.closeButtonAriaLabel,
            modify         : this.modify || this?.currentTarget?.dataset?.modify,
            flatId         : this?.currentTarget?.dataset?.flatId || this.dataContent?.flatId || '',
            planId         : this?.currentTarget?.dataset?.planId || this.dataContent?.planId || ''
        };
    }

    /* @Override */
    // eslint-disable-next-line
    _bindAfterOpenEvents() {}
}

export class AjaxPopupWithHash extends AjaxPopup {
    constructor(options) {
        super(options);

        /* Хэш попапа */
        this.contentLink = null;
    }

    /* @Override */
    _bindOpenEvents() {
        super._bindOpenEvents();

        this._parseUrl();
    }

    /* @Extend */
    displayPopup() {
        super.displayPopup();

        this._setHash();
    }

    /* @Extend */
    removePopup() {
        super.removePopup();

        this._clearHash();
    }

    /* @Override */
    // eslint-disable-next-line no-empty-function
    _onClick() {}

    _parseUrl() {
        this.contentLink = this._getDataAttrValue('data-href');

        if (this.currentState || window.location.hash !== `#${this.contentLink}`) {
            return;
        }

        this._open();
    }

    /**
     * @Private
     * Устанавливает хэш попапа в омнибокс браузера
     */
    _setHash() {
        this.contentLink = this._getDataAttrValue('data-href');

        window.history.pushState(null, null, `#${this.contentLink}`);
    }

    _clearHash() {
        if (window.history.pushState) {
            window.history.pushState(null, null, window.location.pathname + window.location.search);

            return;
        }

        window.location.hash = '';
    }
}

export class ContentPopup extends AjaxPopupWithHash {
    /* @Override */
    _bindOpenEvents() {
        observer.subscribe('popup:open', (popup) => {
            if (openedPopups.has(this.contentLink) &&
                this.currentState &&
                this.contentLink !== popup.contentLink) {
                this._removeOpenedPopup();
            }
        });

        super._bindOpenEvents();
    }

    /* @Override */
    _open() {
        if ((window.location.hash === `#${this.contentLink}` && this.currentState) ||
            openedPopups.has(this.contentLink)) {
            return;
        }

        super._open();
    }

    /* @Override */
    _createPopup() {
        if (!openedPopups.has(this.contentLink)) {
            super._createPopup();
        }
    }

    /* @Extend */
    displayPopup() {
        if (!openedPopups.has(this.contentLink)) {
            super.displayPopup();
            openedPopups.add(this.contentLink);
            // this._initSlider();
        }
    }

    /* @Override */
    _setTemplates() {
        this.template = ajaxTemplates[`${this.templateName}AjaxTemplate`] || defaultAjaxTemplate;
    }

    _removeOpenedPopup() {
        this.popup.classList.remove(this.openStateClass);
        Utils.removeElement(this.popup);

        observer.publish('popup:close', this);

        this.currentState = false;
        openedPopups.delete(this.contentLink);

        document.removeEventListener('keyup', this._closeByKeyboard);
        document.removeEventListener('click', this._closeByMouse);

        this.onClose();
    }

    _clearHash() {
        super._clearHash();
        openedPopups.delete(this.contentLink);
    }
}

export class ConstructorPopup extends AjaxPopup {
    constructor(options) {
        super(options);

        /* Хэш попапа */
        this.contentLink = null;

        /* @Override */
        this.onCreate = this._initConfirmPopupViaFacade.bind(this);
    }

    /* @Override */
    showPopup() {
        const popupScrollElements = this.popup.querySelector('.j-constructor-elements');

        this.popup.classList.remove(this.animationCloseClass);
        this.popup.classList.add(this.openStateClass);

        if (popupScrollElements) {
            setTimeout(() => {
                Utils.bodyFixed(popupScrollElements);
            }, 500);
        }
    }

    _setTemplates() {
        this.template = constructorTemplate;
    }

    /* @Override */
    _sendDataRequest() {
        const that = this;
        const contentLink = this._getDataAttrValue('data-constructor');
        const callback = {
            success(req) {
                that.dataContent = req.data.content;
                that.templateName = req.data.template || 'default';
            },
            error(err) {
                console.error(`Server error with status ${err}`);
            },
            complete() {
                that.dataContent = that.dataContent || that.serverConnection.getErrorMessage();

                that._createPopup();
                that.displayPopup();

                that.dataContent = null;
            }
        };

        Utils.send(null, contentLink, callback, this._getQueryType());
    }

    /* @Override */
    _closeByMouse() {
        return null;
    }

    /* @Override */
    _closeByKeyboard() {
        return null;
    }

    /**
     * Инициализирует кнопки отркрывающие вложенный попап
     */
    _initConfirmPopupViaFacade() {
        const buttons = [...this.popup.querySelectorAll('.j-popup__close')];
        const that = this;

        if (!buttons.length) {
            return;
        }

        const options = {
            ...{
                openButtons: buttons,
                onCreate() {
                    that._bindInnerReturnToParentEvents(this);
                    that._bindInnerCloseParentEvents(this);
                    that.hidePopup();

                    setTimeout(() => {
                        Utils.bodyFixed(that.popup);
                    }, 300);
                }
                // onClose: that.showPopup.bind(that)
            }
        };

        const facade = new PopupFacade(options);

        facade.init();

        buttons.forEach((element) => {
            element.setAttribute('data-init', 'true');
            element.setAttribute('data-init-1', 'true');
        });
    }

    /**
     * @Private
     * Переопределение кнопок закрытия внутреннего попапа
     * Привязка методов к событиям закрытия попапа у внутреннего попапа
     * ВАЖНО: Кнопки закрытия внутреннего попапа НЕ ЗАКРЫВАЮТ основной попап.
     * Наоборот, закрытие внутреннего попапа возвращает основной попап
     * Для закрытия основного попапа используется метод _bindInnerCloseEvents()
     * @param {StaticPopup} context - экземпляр внутреннего попапа
     */
    _bindInnerReturnToParentEvents(context) {
        context.closeBtns = [...context.popup.querySelectorAll('.j-popup__close')];
        context._closeByMouse();
    }

    /**
     * @Private
     * Переопределение кнопок закрытия ОСНОВНОГО попапа у внутреннего попапа
     * Привязка методов к событиям закрытия попапа ОСНОВНОГО попапа у внутреннего попапа
     * @param {StaticPopup} context - экземпляр внутреннего попапа
     */
    _bindInnerCloseParentEvents(context) {
        const actionButtons = [...context.popup.querySelectorAll('.j-popup__close-parent')];

        actionButtons.forEach((button) => {
            button.addEventListener('click', () => {
                context.removePopup();

                if (button.classList.contains('j-uxs-close-constructor') && window.UXS) {
                    window.UXS.sendEvent('userClosedWidget');
                }

                this.removePopup();
            });
        });
    }

    /* @Override */
    displayPopup() {
        this.showPopup();

        initConstructor(this);

        this.currentState = true;
    }
}

export class JsonPopup extends StaticPopup {
    /* @Override */
    _setData() {
        const json = JSON.parse(window.atob(this._getDataAttrValue('data-json')));

        this.data = {
            buttonAriaLabel: this.closeButtonAriaLabel,
            modify         : this.modify,
            content        : json?.data.content,
            title          : json?.data.content.title
        };
    }

    /* @Override */
    _setTemplates() {
        this.template = this.sale ? saleAjaxTemplate : defaultAjaxTemplate;
    }

    /* @Override */
    _open() {
        if (this.sale && !Utils.isMobile()) {
            return;
        }

        this._createPopup();
        this.displayPopup();
    }
}

/**
 * Попап на странице о конструкторе
 */
export class SearcherPopup extends StaticPopup {
    /* @Override */
    _setTemplates() {
        this.template = searcherTemplate;
    }

    _setData() {
        const contentLink = 'about-searcher';

        this.data = {
            content: document.getElementById(contentLink)?.innerHTML
        };
    }

    _bindOpenEvents() {
        this._open();
    }
}

/**
 * Попап на странице отделки
 */
export class FinishingPopup extends StaticPopup {
    /* @Override */
    _setTemplates() {
        this.template = searcherTemplate;
    }

    _setData() {
        const contentLink = 'about-finishing';

        this.data = {
            content: document.getElementById(contentLink)?.innerHTML
        };
    }

    _bindOpenEvents() {
        this._open();
    }
}

/**
 * Попап с формой сохранения результатов конструктора
 */
export class SearcherFormPopup extends StaticPopup {
    constructor(options) {
        super(options);

        this.parentPopup = options.parentPopup;
        this.fromWidget = this._getDataAttrValue('data-from-widget');
    }

    /* eslint-disable max-lines-per-function, max-statements, complexity */
    _setData() {
        const contentLink = this._getDataAttrValue('data-popup');
        const contentBlock = document.getElementById(contentLink);
        const flatID = this._getDataAttrValue('data-flat-id');
        const flatPlan = this._getDataAttrValue('data-flat-plan');
        const title = this._getDataAttrValue('data-title');
        const backLink = this._getDataAttrValue('data-back-link');
        const closeLink = this._getDataAttrValue('data-close-link');
        const flatFloor = this._getDataAttrValue('data-flat-floor');
        const flatBuilding = this._getDataAttrValue('data-flat-building');
        const flatNumber = this._getDataAttrValue('data-flat-number');
        const flatArea = this._getDataAttrValue('data-flat-area');
        const flatFinalArea = this._getDataAttrValue('data-flat-final-area');
        const flatQueue = this._getDataAttrValue('data-flat-queue');
        const flatBasicImg = this._getDataAttrValue('data-flat-basic-img');
        const flatFinalImg = this._getDataAttrValue('data-flat-final-img');
        const flatBasicPlanId = this._getDataAttrValue('data-flat-basic-plan');
        const flatBasicPlanName = this._getDataAttrValue('data-flat-basic-plan-title');
        const flatFinalPlanName = this._getDataAttrValue('data-flat-final-plan-title');
        const flatIDField = contentBlock?.querySelector('input[name="flatID"]');
        const flatIDFieldOld = contentBlock?.querySelector('input.flatID');
        const flatPlanField = contentBlock?.querySelector('input.flatPlan');
        const flatFinalPlanField = contentBlock?.querySelector('input.flatFinalPlan');
        const flatPlanFieldOld = contentBlock?.querySelector('input[name="flatPlan"]');
        const flatFloorField = contentBlock?.querySelector('input.flatFloor');
        const flatAreaField = contentBlock?.querySelector('input.flatArea');
        const flatFinalAreaField = contentBlock?.querySelector('input.flatFinalArea');
        const flatNumberField = contentBlock?.querySelector('input.flatNumber');
        const flatQueueField = contentBlock?.querySelector('input.flatQueue');
        const flatBuildingField = contentBlock?.querySelector('input.flatBuilding');
        const flatBasicImgField = contentBlock?.querySelector('input.flatBasicImg');
        const flatFinalImgField = contentBlock?.querySelector('input.flatFinalImg');
        const flatBasicUrlField = contentBlock?.querySelector('input.flatBasicUrl');
        const flatFinalUrlField = contentBlock?.querySelector('input.flatFinalUrl');

        const flatSavedPlans = contentBlock?.querySelector('input.savedPlans');
        const widgetPlans = window.LayoutBuilder?.getSaved();

        if (flatSavedPlans && widgetPlans) {
            const encodedPlans = JSON.stringify(widgetPlans[widgetPlans.length - 1]);

            flatSavedPlans.value = encodedPlans;
        }

        if (flatIDField && flatID) {
            flatIDField.value = flatID;
        }

        if (flatIDFieldOld && flatID) {
            flatIDFieldOld.value = flatID;
        }

        if (flatPlanField && flatBasicPlanName) {
            // вместо id планировки прокидываем её клиентское название вида "3Е-ТИП-1-1.2-Л/Б-1/3"
            flatPlanField.value = flatBasicPlanName;
        }

        if (flatFinalPlanField && flatFinalPlanName) {
            // вместо id планировки прокидываем её клиентское название вида "3Е-ТИП-1-1.2-Л/Б-1/3"
            flatFinalPlanField.value = flatFinalPlanName;
        }

        if (flatPlanFieldOld && flatPlan) {
            // в старое поле прокидываем id планировки, чтобы pdf приходило по почте
            flatPlanFieldOld.value = flatPlan;
        }

        if (flatFloorField && flatFloor) {
            flatFloorField.value = flatFloor;
        }

        if (flatAreaField && flatArea) {
            flatAreaField.value = flatArea;
        }

        if (flatFinalAreaField && flatFinalArea) {
            flatFinalAreaField.value = flatFinalArea;
        }

        if (flatNumberField && flatNumber) {
            flatNumberField.value = flatNumber;
        }

        if (flatQueueField && flatQueue) {
            flatQueueField.value = flatQueue;
        }

        if (flatBuildingField && flatBuilding) {
            flatBuildingField.value = flatBuilding;
        }

        if (flatBasicImgField && flatBasicImg) {
            flatBasicImgField.value = flatBasicImg;
        }

        if (flatFinalImgField && flatFinalImg) {
            flatFinalImgField.value = `https://${window.location.host}${flatFinalImg}`;
        }

        if (flatBasicUrlField) {
            flatBasicUrlField.value = `https://${window.location.host}/kvartiry/${flatID}?type=${flatBasicPlanId}`;
        }

        if (flatFinalUrlField && closeLink) {
            flatFinalUrlField.value = `https://${window.location.host}${closeLink}`;
        }

        this.data = {
            content: contentBlock?.innerHTML,
            title,
            backLink,
            closeLink
        };

        const closeLinkInPopup = document.querySelector('.j-closelink') || null;

        if (closeLinkInPopup) {
            const link = `/kvartiry/${flatID}`;

            closeLinkInPopup.setAttribute('href', link);
        }
    }
    /* eslint-enable max-lines-per-function, max-statements, complexity */

    showPopup() {
        this.popup.classList.remove(this.animationCloseClass);
        this.popup.classList.add(this.openStateClass);

        const form = this.popup.querySelector('.j-popup-inner-wrapper');

        Utils.bodyFixed(form);
    }

    _bindOpenEvents() {
        if (this.fromWidget) {
            this._open();
        } else {
            this.openBtns.forEach((button, index) => {
                button.addEventListener('click', () => {
                    if (this.currentState) {
                        return;
                    }

                    this.currentTarget = button;

                    if (this.parentPopup) {
                        this.parentPopup.removePopup();
                    }

                    this._open();
                    this._bindAfterOpenEvents(index);
                });
            });
        }
    }

    /* @Extend */
    removePopup() {
        super.removePopup();

        setTimeout(() => {
            observer.publish('searcherPopup:close');
        }, 400);
    }
}

export class Wishes extends StaticPopup {
    constructor(options) {
        super(options);

        this.wish = options.wish;
        this.title = options.title;
    }

    _setData() {
        const title = this.title;
        const contentLink = this._getDataAttrValue('data-popup');
        const contentBlock = document.getElementById(contentLink);
        const wishField = contentBlock?.querySelector('input[name="client-wishes"]') || null;

        if (wishField) {
            wishField.value = this.wish;
        }

        this.data = {
            content: contentBlock?.innerHTML,
            title
        };
    }

    _bindOpenEvents() {
        this._open();
    }
}
